import { useEffect, useMemo, useState } from 'react';
import { call } from '@/api.ts';
import type { AuditEntry } from '@/api.ts';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert.tsx';
import { EmptyState } from '@/components/ui/empty-state.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table.tsx';
import { useAuthenticated } from '@/session.tsx';

/** The audit log: append-only, newest first (§7). */
export function AuditPage() {
  const session = useAuthenticated();
  const [items, setItems] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let live = true;
    call<{ items: AuditEntry[] }>(session, '/audit?limit=100')
      .then((result) => { if (live) setItems(result.items); })
      .catch((cause: Error) => { if (live) setError(cause.message); });
    return () => { live = false; };
  }, [session]);

  const visible = useMemo(() => (items ? filterEntries(items, query) : null), [items, query]);

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load the audit log</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <section className="grid gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[34px] leading-tight font-semibold tracking-[-0.02em]">Audit</h1>
          <p className="text-muted-foreground text-sm">
            Every change to this organization, newest first. Entries are append-only.
          </p>
        </div>

        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Filter by actor, action, or target"
          aria-label="Filter audit entries"
          className="w-[260px] max-w-full"
        />
      </header>

      <div>
        {!visible ? (
          <div className="grid gap-3 p-4">
            {[0, 1, 2, 3].map((row) => <Skeleton key={row} className="h-4 w-full" />)}
          </div>
        ) : visible.length === 0 ? (
          <EmptyState
            title={query ? 'No entries match that filter' : 'Nothing recorded yet'}
            description={
              query
                ? 'Try a shorter search, or clear the filter.'
                : 'Changes made through the console or the API appear here as they happen.'
            }
          />
        ) : (
          <Table>
            <colgroup>
              <col className="w-[180px]" />
              <col className="w-[230px]" />
              <col className="w-[190px]" />
              <col />
              <col className="w-[120px]" />
            </colgroup>
            <TableHeader className="[&_tr]:border-foreground [&_tr]:border-b">
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Change</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((entry) => {
                const expandable = entry.before !== undefined || entry.after !== undefined;
                const expanded = open === entry.SK;
                return (
                  <>
                    <TableRow key={entry.SK}>
                      <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
                        {new Date(entry.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-muted-foreground font-mono text-[12.5px]">{entry.actor}</TableCell>
                      {/* A refused action is the interesting one: it is the record of something
                          that did not happen, next to the attempt that did. */}
                      <TableCell
                        className={[
                          'font-mono text-[12.5px]',
                          isRefusal(entry.action) ? 'text-destructive' : '',
                        ].join(' ')}
                      >
                        {entry.action}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{entry.target}</TableCell>
                      <TableCell>
                        {expandable && (
                          <button
                            type="button"
                            onClick={() => setOpen(expanded ? null : entry.SK)}
                            aria-expanded={expanded}
                            className="text-primary text-[13px] hover:underline"
                          >
                            {expanded ? 'Hide' : 'View'}
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                    {expanded && (
                      <TableRow key={`${entry.SK}-detail`}>
                        <TableCell colSpan={5} className="bg-muted px-5 py-4">
                          {/* The before/after was always recorded; the old table hid it behind
                              the word "updated", which is the least useful summary available. */}
                          <div className="grid gap-8 md:grid-cols-2">
                            <ChangeSide label="Before" value={entry.before} />
                            <ChangeSide
                              label="After"
                              value={entry.after}
                              highlight={changedLines(entry.before, entry.after)}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <p className="text-muted-foreground max-w-[70ch] text-[12.5px]">
        Console sessions record as <code className="font-mono">user:</code>, API tokens as{' '}
        <code className="font-mono">token:</code>. An IdP client secret is absent from every entry
        by construction — it is never on the record it would be copied from.
      </p>
    </section>
  );
}

function ChangeSide({ label, value, highlight }: { label: string; value: unknown; highlight?: ReadonlySet<number> }) {
  const text = value === undefined ? '—' : stableJson(value);
  return (
    <div className="min-w-0">
      <p className="text-muted-foreground mb-1.5 text-[11px] font-medium tracking-[0.12em] uppercase">{label}</p>
      <pre className="overflow-x-auto font-mono text-xs leading-[1.6]">
        {text.split('\n').map((line, index) => (
          <div
            key={index}
            className={highlight?.has(index) ? 'bg-primary/15' : undefined}
          >
            {line || ' '}
          </div>
        ))}
      </pre>
    </div>
  );
}

/** `channel.promote.refused` and its kin — the record of something that was denied. */
export function isRefusal(action: string): boolean {
  return action.endsWith('.refused') || action.endsWith('.denied');
}

/**
 * Which lines of *after* the reader should look at first.
 *
 * Compared as a multiset rather than index by index. A single inserted line shifts every line
 * below it, and an index comparison would then highlight the whole remainder of the document —
 * which highlights nothing, because a panel where everything is marked has marked nothing.
 */
export function changedLines(before: unknown, after: unknown): ReadonlySet<number> {
  if (after === undefined) return new Set();
  if (before === undefined) return new Set();

  const remaining = new Map<string, number>();
  for (const line of stableJson(before).split('\n')) {
    remaining.set(line, (remaining.get(line) ?? 0) + 1);
  }

  const changed = new Set<number>();
  stableJson(after).split('\n').forEach((line, index) => {
    const left = remaining.get(line) ?? 0;
    if (left > 0) remaining.set(line, left - 1);
    else changed.add(index);
  });

  return changed;
}

/**
 * Both panes are rendered key-sorted, and that is what makes the diff readable.
 *
 * Key order is not a change — it is the same notion of equality a snapshot id is derived from.
 * Left alone it is worse than noise: reordering two keys moves the trailing comma between them,
 * so a record that changed in no way at all highlights two lines.
 */
export function stableJson(value: unknown): string {
  return JSON.stringify(sortKeys(value), null, 2);
}

function sortKeys(value: unknown): unknown {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(sortKeys);
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => [key, sortKeys(item)]),
  );
}

/** Matches the three columns an operator scans: who, what, and what it was done to. */
export function filterEntries(entries: AuditEntry[], query: string): AuditEntry[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return entries;
  return entries.filter((entry) =>
    entry.actor.toLowerCase().includes(needle) ||
    entry.action.toLowerCase().includes(needle) ||
    entry.target.toLowerCase().includes(needle));
}
